

























import {
  Vue,
  Component,
  Prop,
} from "@/shared/vuepack";
import _, { Dictionary } from "lodash";
import InstrumentSearchResultsTable, { InstrumentSearchResult } from "./InstrumentSearchResultsTable.vue";
import GroupedInstrumentsTable from "./GroupedInstrumentsTable.vue";
import {
  SelectedInstrument, AdvisoryFund, DrivewealthAccountManagementType
} from "@/shared/types";
import * as drivewealth from "@/shared/custodianSettings/drivewealthTypes";
import { useDrivewealthStore } from "@/shared/store/drivewealth";
import { useDrivewealthSweepStore } from "@/shared/store/drivewealthSweep";

const maxTableRows = 40;
const UncategorizedAssetCategory = "no_cat";

@Component({
  components: {
    InstrumentSearchResultsTable,
    GroupedInstrumentsTable,
  }
})
export default class SelectListInstrumentSearch extends Vue {
  searchTerm: string = null;

  @Prop({ required: false }) username: string;
  @Prop({ required: false }) partnerId: number;
  @Prop({ required: false }) drivewealthAccountId: number;

  drivewealthStore = useDrivewealthStore();
  drivewealthSweepStore = useDrivewealthSweepStore();

  statusMap = drivewealth.statusMap;

  mounted () {
    this.drivewealthStore.loadAdvisoryFunds({ username: this.username, partnerId: this.partnerId, force: true });
  }

  get noSearchResults () {
    return !!this.searchTerm && this.instrumentSearchResults.length === 0;
  }

  get showBusyState () {
    return this.noSearchResults || !this.drivewealthStore.advisoryFundsLoaded;
  }
  get showAllFunds () {
    return this.drivewealthSweepStore.showAllAdvisoryFunds;
  }

  set showAllFunds (value: boolean) {
    this.drivewealthSweepStore.setShowAllAdvisoryFunds(value);
  }

  mapToSelectedInstrument (d: AdvisoryFund): SelectedInstrument {
    return {
      instrumentId: d.instrumentId,
      stockSymbol: d.stockSymbol,
      companyName: d.company,
      website: d.website,
      imageUrl: d.imageUrl,
      status: d.status,
      currentPrice: d.marketPrice,
      allocation: 0,
      drivewealthAccountId: this.drivewealthAccountId ? this.drivewealthAccountId : 0,
      accountType: DrivewealthAccountManagementType.Advisory
    };
  }

  get fundItems () {
    if (!this.drivewealthStore.advisoryFunds) {
      return [];
    }

    return this.drivewealthStore.advisoryFunds.map(f => ({
      instrument: { symbol: f.stockSymbol, companyName: f.company, company: f.company, website: f.website, imageUrl: f.imageUrl },
      status: this.statusMap[f.status],
      fund_type: this.drivewealthStore.fundTypeDisplayCopy(f.fundType),
      action: this.mapToSelectedInstrument(f),
      stockSymbol: f.stockSymbol,
      company: f.company,
      assetCategoryName: f.assetCategoryName,
      allocationPercentage: f.allocationPercentage,
    }));
  }

  get groupedInstrumentDetails (): Dictionary<any[]> {

    const orderedResult = _.orderBy(this.fundItems, "allocationPercentage", "desc");
    const groupedResult = _.groupBy(orderedResult, item => {
      return _.get(item, "assetCategoryName", UncategorizedAssetCategory);
    });

    return groupedResult;
  }

  get instrumentSearchResults (): any[] {
    if (!this.fundItems) { return []; }
    if (!this.searchTerm) { return this.showAllFunds ? this.fundItems : []; }

    return this.fundItems
      .map(fund => ({ fund, score: this.searchScore(fund) }))
      .filter(pair => this.showAllFunds || pair.score > 0)
      .sort((a, b) => b.score - a.score)
      .map(pair => pair.fund);
  }

  searchScore (fund: any): number {
    const ucSearchTerm = this.searchTerm.toUpperCase();
    if (fund.stockSymbol === ucSearchTerm) { return 5; }
    if (fund.stockSymbol.startsWith(ucSearchTerm)) { return 4; }
    if (fund.stockSymbol.includes(ucSearchTerm)) { return 3; }
    if (fund.company.toUpperCase().startsWith(ucSearchTerm)) { return 2; }
    if (fund.company.toUpperCase().includes(ucSearchTerm)) { return 1; }

    return -1;
  }

  get detailItems (): InstrumentSearchResult[] {
    if (!this.instrumentSearchResults) {
      return [];
    }
    return this.instrumentSearchResults.slice(0, maxTableRows);
  }

  get disableSearch (): boolean {
    return !this.drivewealthSweepStore.isActive || this.showAllFunds;
  }
}
